import {
  hiddenSaleLineItemProperties,
  hiddenSalePrice,
  metafieldValue,
  parseJson,
} from '@lyle/algolia-product-utils';
import { bindProductColourNav } from '@lyle/product-colour-nav';
import { getLocalisedRoute } from '@lyle/weglot';

class LyleMainProduct extends HTMLElement {
  #variants = [];
  #tags = [];
  #inventory = {};
  #changeHandler = null;
  #sizeBtnHandler = null;
  #siblingHandler = null;
  #submitHandler = null;
  #buyNowHandler = null;
  #atcResetTimer = null;
  #formatter = null;
  #llCustomerTierId = null;

  connectedCallback() {
    bindProductColourNav();

    const script = this.querySelector('[data-product-variants-json]');
    if (script) {
      try {
        this.#variants = JSON.parse(script.textContent);
      } catch {
        this.#variants = [];
      }
    }

    const invScript = this.querySelector('[data-product-inventory-json]');
    if (invScript) {
      try {
        this.#inventory = JSON.parse(invScript.textContent);
      } catch {
        this.#inventory = {};
      }
    }

    const tagsScript = this.querySelector('[data-product-tags-json]');
    this.#tags = parseJson(tagsScript?.textContent) || [];

    this.#initLoyaltyBanner();

    if (this.#variants.length > 0) {
      this.#changeHandler = (e) => {
        if (e.target.matches('[data-option-position]')) this.#onOptionChange();
      };
      this.addEventListener('change', this.#changeHandler);

      // Size buttons drive the hidden select; the existing #changeHandler does the rest.
      this.#sizeBtnHandler = (e) => {
        const btn = e.target.closest('[data-size-btn]');
        // Sold-out sizes stay clickable (legacy); only skip native disabled.
        if (!btn || btn.disabled) return;

        const optionPosition = btn.dataset.optionPosition;
        const value = btn.dataset.optionValue;
        if (optionPosition == null || value == null) return;

        const select = this.querySelector(
          `[data-option-position="${optionPosition}"].main-product__option-select--hidden`,
        );
        if (select) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const group = btn.closest('[data-size-buttons]');
        if (group) {
          group.querySelectorAll('[data-size-btn]').forEach((b) => {
            const active = b.dataset.optionValue === value;
            b.setAttribute('aria-pressed', String(active));
            b.toggleAttribute('data-selected', active);
          });
        }
      };
      this.addEventListener('click', this.#sizeBtnHandler);
    }

    const form = this.querySelector('#main-product-form');
    if (form) {
      this.#submitHandler = (e) => {
        e.preventDefault();
        this.#handleAddToCart().catch(() => {});
      };
      form.addEventListener('submit', this.#submitHandler);
    }

    const buyNowBtn = this.querySelector('[data-buy-now]');
    if (buyNowBtn) {
      this.#buyNowHandler = () => this.#handleBuyNow().catch(() => {});
      buyNowBtn.addEventListener('click', this.#buyNowHandler);
    }

    // Fires after Algolia returns the colour-family product list.
    this.#siblingHandler = (e) => this.#renderSiblingGroups(e.detail);
    document.addEventListener('colour-swatches:siblings', this.#siblingHandler);

    if (this.#variants.length > 0) {
      const initialVariant = this.#getSelectedVariant();
      if (this.#hiddenSalePrice() !== null) this.#updatePrice(initialVariant);
      const hasSizeOption = !!this.querySelector('[data-size-buttons]');
      const hasVariantParam = new URLSearchParams(window.location.search).has('variant');

      // Only treat a size as selected when the URL already has ?variant= (or there
      // is no size option). Avoid marking the Liquid default select value as chosen.
      if (!hasSizeOption || hasVariantParam) {
        this.#updateSizeBtnActiveState(initialVariant);
        this.#updateButton(initialVariant);
        this.#updateBuyNowVisibility(initialVariant);
        this.#updateUrl(initialVariant);
      } else {
        this.#updateBuyNowVisibility(null);
      }

      this.#updateStockIndicator(
        !hasSizeOption || hasVariantParam ? initialVariant : null,
      );
      this.#broadcastVariant(
        !hasSizeOption || hasVariantParam ? initialVariant : null,
      );
    }
  }

  disconnectedCallback() {
    if (this.#changeHandler) {
      this.removeEventListener('change', this.#changeHandler);
      this.#changeHandler = null;
    }
    if (this.#sizeBtnHandler) {
      this.removeEventListener('click', this.#sizeBtnHandler);
      this.#sizeBtnHandler = null;
    }

    const form = this.querySelector('#main-product-form');
    if (form && this.#submitHandler) {
      form.removeEventListener('submit', this.#submitHandler);
    }
    this.#submitHandler = null;

    if (this.#atcResetTimer) {
      clearTimeout(this.#atcResetTimer);
      this.#atcResetTimer = null;
    }

    if (this.#siblingHandler) {
      document.removeEventListener(
        'colour-swatches:siblings',
        this.#siblingHandler,
      );
      this.#siblingHandler = null;
    }

    if (this.#buyNowHandler) {
      this.querySelector('[data-buy-now]')?.removeEventListener('click', this.#buyNowHandler);
      this.#buyNowHandler = null;
    }

    this.#variants = [];
    this.#inventory = {};
    this.#formatter = null;
  }

  #onOptionChange() {
    const variant = this.#getSelectedVariant();
    this.#updateSizeBtnActiveState(variant);
    this.#updateVariantInput(variant);
    this.#updateUrl(variant);
    this.#updatePrice(variant);
    this.#updateButton(variant);
    this.#broadcastVariant(variant);
    this.#updateStockIndicator(variant);
  }

  async #handleAddToCart() {
    if (this.dataset.comingSoon === 'true') {
      const link = this.dataset.comingSoonLink;
      if (link) window.location.href = getLocalisedRoute(link);
      return;
    }

    const selected = this.#getSelectedVariant();
    if (!selected?.available) return;

    const variantId = this.querySelector('[data-variant-id-input]')?.value;
    if (!variantId) return;

    const quantity = 1;

    const btn = this.querySelector('[data-add-to-cart]');
    const buyNow = this.querySelector('[data-buy-now]');
    const errorEl = this.querySelector('[data-product-form-error]');

    const addingText = this.dataset.addingText ?? '';
    if (btn) {
      this.#setButtonDisabled(btn, true);
      if (addingText) {
        const textEl = btn.querySelector('.button__label') ?? btn.querySelector('[data-atc-text]') ?? btn;
        textEl.textContent = addingText;
      }
    }
    if (buyNow) this.#setButtonDisabled(buyNow, true);
    if (errorEl) errorEl.hidden = true;

    document.dispatchEvent(
      new CustomEvent('cart:add', {
        bubbles: false,
        detail: { optimistic: this.#optimisticCartItem(selected, quantity) },
      }),
    );

    try {
      const res = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity,
          properties: this.#lineItemProperties(),
          sections: 'cart-drawer',
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.description ?? this.dataset.errorText ?? '');
      }

      const cart = await res.json();

      const addedText = this.dataset.addedText ?? this.dataset.addToCartText ?? '';
      if (btn) {
        const textEl = btn.querySelector('.button__label') ?? btn.querySelector('[data-atc-text]') ?? btn;
        textEl.textContent = addedText;
      }

      document.dispatchEvent(
        new CustomEvent('cart:add', {
          bubbles: false,
          detail: { sections: cart.sections },
        })
      );

      if (this.#atcResetTimer) clearTimeout(this.#atcResetTimer);
      this.#atcResetTimer = setTimeout(() => {
        this.#atcResetTimer = null;
        const variant = this.#getSelectedVariant();
        this.#updateButton(variant);
      }, 2000);
    } catch (err) {
      document.dispatchEvent(
        new CustomEvent('cart:add', {
          bubbles: false,
          detail: { rollback: true },
        }),
      );
      const variant = this.#getSelectedVariant();
      this.#updateButton(variant);
      if (errorEl) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      }
    }
  }

  #optimisticCartItem(variant, quantity) {
    const title =
      this.querySelector('.main-product__title')?.textContent?.trim() ||
      document.querySelector('.breadcrumbs__label-text')?.textContent?.trim() ||
      document.title;
    const galleryImage = document.querySelector(
      `[data-media-id="${CSS.escape(String(variant.featured_image?.id ?? ''))}"] img`,
    ) ?? document.querySelector('[data-product-gallery-carousel] img');
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));

    return {
      imageAlt: variant.featured_image?.alt || galleryImage?.alt || title,
      imageUrl: variant.featured_image?.src || galleryImage?.currentSrc || galleryImage?.src || '',
      options: variant.options || [],
      price: this.#formatMoney(this.#hiddenSalePrice() ?? this.#memberDisplayPrice(variant)),
      quantity,
      title,
      url: url.toString(),
    };
  }

  async #handleBuyNow() {
    const selected = this.#getSelectedVariant();
    if (!selected?.available) return;

    const variantId = this.querySelector('[data-variant-id-input]')?.value;
    if (!variantId) return;

    const btn = this.querySelector('[data-buy-now]');
    const atcBtn = this.querySelector('[data-add-to-cart]');
    const errorEl = this.querySelector('[data-product-form-error]');
    if (btn) this.#setButtonDisabled(btn, true);
    if (atcBtn) this.#setButtonDisabled(atcBtn, true);
    if (errorEl) errorEl.hidden = true;

    try {
      const res = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          properties: this.#lineItemProperties(),
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.description ?? this.dataset.errorText ?? '');
      }

      window.location.href = `${window.Shopify?.routes?.root ?? '/'}checkout`;
    } catch (err) {
      const variant = this.#getSelectedVariant();
      this.#updateButton(variant);
      if (errorEl) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      }
    }
  }

  #lineItemProperties() {
    const form = this.querySelector('#main-product-form');
    const properties = hiddenSaleLineItemProperties(this.#tags);

    if (!form) return properties;

    for (const [name, value] of new FormData(form)) {
      const match = name.match(/^properties\[(.+)]$/);
      if (match && value !== '') properties[match[1]] = value;
    }

    return properties;
  }

  // Note: ProductSize- must be checked exclusively — ProductSizeFamily- also starts with
  // "ProductSize-", so the filter below explicitly excludes it.
  #renderSiblingGroups({ products, currentHandle }) {
    const container = this.querySelector('[data-size-siblings]');
    if (!container) return;

    const currentProduct = products.find((p) => p.handle === currentHandle);
    if (!currentProduct) return;

    const sizeFamilyTag =
      currentProduct.tags?.find((t) => t.startsWith('ProductSizeFamily-')) ?? '';
    if (!sizeFamilyTag) return;

    const currentSwatchCode = metafieldValue(currentProduct, 'swatch_code');
    if (!currentSwatchCode) return;

    const sizeProducts = products.filter(
      (p) =>
        p.tags?.includes(sizeFamilyTag) &&
        metafieldValue(p, 'swatch_code') === currentSwatchCode,
    );

    if (!sizeProducts.length) return;

    const seen = new Set();
    const toggleItems = [];

    for (const p of sizeProducts) {
      const sizeTag =
        p.tags?.find(
          (t) => t.startsWith('ProductSize-') && !t.startsWith('ProductSizeFamily-'),
        ) ?? '';
      const label = sizeTag ? sizeTag.split('-')[1] : '';
      if (!label || seen.has(label)) continue;
      seen.add(label);
      toggleItems.push({ label, url: p.url, isActive: p.handle === currentHandle });
    }

    if (toggleItems.length <= 1) return;

    // Reverse-alpha so "Standard" appears before "Plus".
    toggleItems.sort((a, b) => b.label.localeCompare(a.label));

    let html = '<div class="main-product__size-toggle">';
    for (const { label, url, isActive } of toggleItems) {
      if (isActive) {
        html += `<button class="main-product__size-toggle-btn is-active text-body-sm" aria-pressed="true" aria-current="page" disabled>${LyleMainProduct.#esc(label)}</button>`;
      } else {
        const safeHref = /^https?:\/\/|^\//i.test(url) ? LyleMainProduct.#esc(url) : '#';
        html += `<a class="main-product__size-toggle-btn text-body-sm" href="${safeHref}" data-product-colour-nav>${LyleMainProduct.#esc(label)}</a>`;
      }
    }
    html += '</div>';

    container.hidden = false;
    container.innerHTML = html;
  }

  // Also called on colour change, to keep size buttons in sync.
  #updateSizeBtnActiveState(variant) {
    if (!variant) return;

    this.querySelectorAll('[data-size-buttons]').forEach((group) => {
      const position = parseInt(group.dataset.optionPosition ?? '0', 10);
      const selectedValue =
        variant.options?.[position] ?? variant[`option${position + 1}`] ?? '';

      group.querySelectorAll('[data-size-btn]').forEach((btn) => {
        const active = btn.dataset.optionValue === selectedValue;
        btn.setAttribute('aria-pressed', String(active));
        btn.toggleAttribute('data-selected', active);
      });

      const labelEl = group
        .closest('.main-product__option-group')
        ?.querySelector('[data-size-label]');
      if (labelEl && selectedValue) {
        const optionName = labelEl.dataset.sizeOptionName ?? '';
        labelEl.textContent = `${optionName}: ${selectedValue}`;
      }
    });
  }

  #updateStockIndicator(variant) {
    const el = this.querySelector('[data-stock-indicator]');
    if (!el) return;

    const threshold = parseInt(this.dataset.lowStockThreshold ?? '10', 10);
    const invData = variant ? (this.#inventory[variant.id] ?? null) : null;
    const inv = invData?.quantity ?? variant?.inventory_quantity ?? 0;
    const managed = (invData?.management ?? variant?.inventory_management) === 'shopify';
    const policy = invData?.policy ?? variant?.inventory_policy ?? '';
    const available = variant?.available ?? false;
    const showSoldOut = variant && !available;
    const showWarning = managed && policy !== 'continue' && available && inv > 0 && inv <= threshold;

    el.hidden = !showWarning && !showSoldOut;
    el.classList.toggle('stock-indicator--low', showWarning);
    el.classList.toggle('stock-indicator--out', showSoldOut);
    if (showWarning) {
      const template = this.dataset.lowStockText ?? '';
      el.textContent = template.replace('__COUNT__', String(inv));
    } else if (showSoldOut) {
      el.textContent = this.dataset.soldOutText ?? '';
    } else {
      el.textContent = '';
    }
  }

  #broadcastVariant(variant) {
    document.dispatchEvent(
      new CustomEvent('product-gallery:variant', {
        detail: { mediaId: variant?.featured_image?.id ?? null },
      }),
    );

    const hasSizeOption = !!this.querySelector('[data-size-buttons]');
    const sizeSelected = !!this.querySelector('[data-size-btn][data-selected]');
    const hasVariantParam = new URLSearchParams(window.location.search).has('variant');
    const optionValues = [];

    if (!hasSizeOption || sizeSelected || hasVariantParam) {
      if (variant) {
        for (let i = 1; i <= 3; i += 1) {
          const value = variant[`option${i}`];
          if (value) optionValues.push(value);
        }
      }

      // Prefer the explicitly selected size button label when present.
      const selectedBtn = this.querySelector('[data-size-btn][data-selected]');
      if (selectedBtn?.dataset.optionValue) {
        const sizeValue = selectedBtn.dataset.optionValue;
        if (!optionValues.includes(sizeValue)) {
          optionValues.unshift(sizeValue);
        }
      }
    }

    document.dispatchEvent(
      new CustomEvent('product:variant-change', {
        bubbles: false,
        detail: {
          variant,
          optionValues,
        },
      }),
    );
  }

  #getSelectedVariant() {
    if (this.#variants.length === 0) return null;
    const selects = this.querySelectorAll('select[data-option-position]');
    if (!selects.length) return this.#variants[0] ?? null;
    const opts = Array.from(selects).map((s) => s.value);
    return (
      this.#variants.find((v) => opts.every((opt, i) => v[`option${i + 1}`] === opt)) ??
      null
    );
  }

  #updateVariantInput(variant) {
    const input = this.querySelector('[data-variant-id-input]');
    if (input) input.value = variant?.id ?? '';
  }

  #updateUrl(variant) {
    const url = new URL(window.location.href);

    if (variant?.id) {
      url.searchParams.set('variant', String(variant.id));
    } else {
      url.searchParams.delete('variant');
    }

    history.replaceState(history.state || {}, '', url.toString());
  }

  #updateBuyNowVisibility(variant) {
    const buyNow = this.querySelector('[data-buy-now]');
    if (!buyNow) return;

    if (this.dataset.comingSoon === 'true') {
      buyNow.hidden = true;
      this.#setButtonDisabled(buyNow, true);
      return;
    }

    const hasSizeOption = !!this.querySelector('[data-size-buttons]');
    const sizeSelected = !!this.querySelector('[data-size-btn][data-selected]');
    const show = (!hasSizeOption || sizeSelected) && Boolean(variant?.available);

    buyNow.hidden = !show;
    this.#setButtonDisabled(buyNow, !show);
  }

  #isSizeSelected() {
    const hasSizeOption = !!this.querySelector('[data-size-buttons]');
    if (!hasSizeOption) return true;
    return !!this.querySelector('[data-size-btn][data-selected]');
  }

  #updatePrice(variant) {
    if (!variant) return;
    const html = this.#buildPriceHtml(variant);
    const main = this.querySelector('[data-main-product-prices]');
    if (main) main.innerHTML = html;
    const memberBadge = this.querySelector('[data-member-price-badge]');
    if (memberBadge) memberBadge.hidden = !this.#hasMemberPrice(variant);
    const klarnaAmount = this.querySelector('[data-klarna-amount]');
    if (klarnaAmount && variant?.price != null) {
      const display = this.#memberDisplayPrice(variant);
      klarnaAmount.textContent = this.#formatMoney(Math.round(display / 3));
    }
    this.#updateLionPrice(variant);
    this.#updateLoyaltyBanner(variant);
  }

  #memberDiscount() {
    return Number(this.dataset.memberDiscount || 0);
  }

  #isCustomer() {
    return this.dataset.isCustomer === 'true';
  }

  #hasMemberPrice(variant) {
    const discount = this.#memberDiscount();
    return (
      this.#isCustomer() &&
      discount > 0 &&
      variant?.price != null
    );
  }

  #memberDisplayPrice(variant) {
    const price = variant?.price;
    if (!this.#hasMemberPrice(variant)) return price;
    return Math.round(price - price * this.#memberDiscount());
  }

  #hiddenSalePrice() {
    const price = hiddenSalePrice(this.#tags, window.Shopify?.currency?.active);
    return price === null ? null : Math.round(price * 100);
  }

  #buildPriceHtml(variant) {
    if (!variant) return '';

    const available = variant.available;
    let price = variant.price;
    let compareAt = variant.compare_at_price;
    const hasMemberPrice = this.#hasMemberPrice(variant);

    if (hasMemberPrice) {
      // The previous price stays the full price, not the sale price the member
      // discount comes off.
      compareAt = compareAt && compareAt > price ? compareAt : price;
      price = this.#memberDisplayPrice(variant);
    }

    const salePrice = this.#hiddenSalePrice();
    if (salePrice !== null) {
      compareAt = price;
      price = salePrice;
    }

    const onSale = compareAt && price < compareAt;

    let compareHtml = '';
    if (onSale) {
      compareHtml = `<span class="product-prices__compare-at-price main-product__compare-at-price">${this.#formatMoney(compareAt)}</span>`;
    }

    const priceClass = onSale && !hasMemberPrice
      ? 'product-prices__price product-prices__price--on-sale main-product__price main-product__price--on-sale'
      : 'product-prices__price main-product__price';

    let priceHtml;
    if (price <= 0) {
      priceHtml = `<span class="${priceClass}">${LyleMainProduct.#esc(window.Shopify?.i18n?.translations?.products?.prices?.free ?? 'Free')}</span>`;
    } else {
      priceHtml = `<span class="${priceClass}">${this.#formatMoney(price)}</span>`;
    }

    return `<p class="product-prices main-product__prices text-body-lg${hasMemberPrice ? ' product-prices--member' : ''}">${compareHtml}${priceHtml}</p>`;
  }

  #updateLionPrice(variant) {
    const el = this.querySelector('[data-lion-price]');
    if (!el || variant?.price == null) return;
    el.textContent = this.#formatMoney(this.#memberDisplayPrice(variant));
  }

  #initLoyaltyBanner() {
    const banner = this.querySelector('[data-loyalty-pdp-banner]');
    if (!banner) return;

    const variant = this.#getSelectedVariant() ?? this.#variants[0];
    this.#updateLoyaltyBanner(variant);

    if (!this.#isCustomer()) return;

    if (window._llCustomerDataReady) {
      this.#applyCustomerLoyaltyData();
      return;
    }

    window._llCustomerDataCallbacks = window._llCustomerDataCallbacks || [];
    window._llCustomerDataCallbacks.push(() => this.#applyCustomerLoyaltyData());
  }

  #applyCustomerLoyaltyData() {
    this.#llCustomerTierId = window._llCustomerTierId ?? null;
    const variant = this.#getSelectedVariant() ?? this.#variants[0];
    this.#updateLoyaltyBanner(variant);
  }

  #loyaltyTierRate() {
    const tierId = this.#llCustomerTierId ?? window._llCustomerTierId;
    const mapped = tierId && window._llTierPointsMap?.[String(tierId)];
    if (mapped != null) return Number(mapped);
    return Number(this.dataset.loyaltyTier1Points || 3);
  }

  #updateLoyaltyBanner(variant) {
    const banner = this.querySelector('[data-loyalty-pdp-banner]');
    if (!banner) return;

    const price = variant?.price ?? 0;
    const rate = this.#loyaltyTierRate();
    const earnPoints = price ? Math.round((price / 100) * rate) : 0;

    const earnEl = banner.querySelector('[data-loyalty-earn]');
    if (earnEl) earnEl.textContent = String(earnPoints);

    const pointValueEl = banner.querySelector('[data-ll-point-value]');
    if (pointValueEl) {
      const currency = window.Shopify?.currency?.active || window._llBaseCurrency || 'GBP';
      const lang = document.documentElement.lang || 'en';
      const ratio = parseFloat(window.Shopify?.currency?.rate) || 1;
      const formatted = new Intl.NumberFormat(lang, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(ratio);
      const pointsLabel = this.dataset.loyaltyPointsLabel || 'points';
      pointValueEl.textContent = `${rate} ${pointsLabel} = ${formatted}`;
    }

    if (!this.#isCustomer()) return;

    const tierId = this.#llCustomerTierId ?? window._llCustomerTierId;
    const tierName = (tierId && window._llTierMap?.[String(tierId)]) || '';

    const balanceEl = banner.querySelector('[data-loyalty-balance]');
    if (balanceEl && window._llCustomerPoints !== undefined) {
      balanceEl.textContent = new Intl.NumberFormat(
        document.documentElement.lang || 'en',
      ).format(window._llCustomerPoints);
    }

    const color =
      (tierId && window._llTierColorMap?.[String(tierId)]) ||
      (tierName && window._llTierNameColorMap?.[tierName]) ||
      '';
    if (color) banner.style.backgroundColor = color;

    if (tierName && window._llCustomerPoints !== undefined) {
      banner.querySelector('[data-loyalty-balance-line]')?.removeAttribute('hidden');
    }
  }

  #updateButton(variant) {
    this.#setButtonFromVariant(this.querySelector('[data-add-to-cart]'), variant);
    this.#updateBuyNowVisibility(variant);
  }

  #setButtonFromVariant(btn, variant) {
    if (!btn) return;
    const textEl = btn.querySelector('.button__label') ?? btn.querySelector('[data-atc-text]') ?? btn;

    if (this.dataset.comingSoon === 'true') {
      const link = this.dataset.comingSoonLink || '';
      this.#setButtonDisabled(btn, !link);
      textEl.textContent = this.dataset.comingSoonText ?? '';
      return;
    }

    const hasSizeOption = !!this.querySelector('[data-size-buttons]');
    const sizeSelected = this.#isSizeSelected();

    if (hasSizeOption && !sizeSelected) {
      this.#setButtonDisabled(btn, true);
      this.#setButtonUnavailableSize(btn, false);
      textEl.textContent = this.dataset.selectSizeText ?? '';
      return;
    }

    if (!variant) {
      this.#setButtonDisabled(btn, true);
      this.#setButtonUnavailableSize(btn, false);
      textEl.textContent = this.dataset.selectSizeText ?? '';
      return;
    }

    if (!variant.available) {
      this.#setButtonDisabled(btn, true);
      this.#setButtonUnavailableSize(btn, true);
      textEl.textContent = this.dataset.unavailableText ?? '';
      return;
    }

    this.#setButtonDisabled(btn, false);
    this.#setButtonUnavailableSize(btn, false);
    textEl.textContent = this.dataset.addToCartText ?? '';
  }

  #setButtonDisabled(btn, disabled) {
    if (!btn) return;
    btn.disabled = disabled;
    btn.classList.toggle('is-disabled', disabled);
  }

  #setButtonUnavailableSize(btn, active) {
    if (!btn) return;
    btn.classList.toggle('is-unavailable-size', active);
    btn.classList.toggle('button--no-icon', active);
    const icon = btn.querySelector('.button__icon');
    if (icon) icon.hidden = active;
    btn.toggleAttribute('aria-disabled', active);
  }

  #formatMoney(cents) {
    if (!this.#formatter) {
      const currency = window.Shopify?.currency?.active ?? 'GBP';
      this.#formatter = new Intl.NumberFormat(
        window.Shopify?.locale ?? 'en-GB',
        { style: 'currency', currency, minimumFractionDigits: 2 },
      );
    }
    return this.#formatter.format(cents / 100);
  }

  static #esc(s) {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

if (!customElements.get('lyle-main-product')) {
  customElements.define('lyle-main-product', LyleMainProduct);
}
